import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { ShoppingBag } from "lucide-react";
import { Header } from "@/components/pizzaria/Header";
import { Menu } from "@/components/pizzaria/Menu";
import { CartDrawer } from "@/components/pizzaria/CartDrawer";
import { BordaModal } from "@/components/pizzaria/BordaModal";
import { MeioMeioModal } from "@/components/pizzaria/MeioMeioModal";
import { RESTAURANT } from "@/data/menu";

const formatBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const isOpenNow = () => {
  const now = new Date();
  const day = now.getDay();
  const hm = now.getHours() * 60 + now.getMinutes();
  const open = 18 * 60 + 15;
  if (day === 2) return false;
  if (day === 5 || day === 6) return hm >= open && hm <= 23 * 60 + 59;
  if (day === 0) return hm >= open && hm <= 23 * 60 + 30;
  return hm >= open && hm <= 23 * 60;
};

function App() {
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [bordaPayload, setBordaPayload] = useState(null);
  const [meioOpen, setMeioOpen] = useState(false);
  const open = useMemo(() => isOpenNow(), []);

  useEffect(() => {
    document.title = `${RESTAURANT.name} — Delivery em Londrina`;
  }, []);

  const requestAdd = (payload) => setBordaPayload(payload);

  const openMeioMeio = () => setMeioOpen(true);

  const handleMeioPicked = ({ base, other }) => {
    // Build a synthetic "item" using base pricing (higher price)
    const syntheticItem = {
      name: `${base.name} / ${other.name}`,
      description: `Meio ${base.name}, meio ${other.name}. Cobrado pelo sabor mais caro (${base.tier}).`,
      image: base.image,
      prices: base.prices,
      tier: base.tier,
      flavorType: base.flavorType,
    };
    setBordaPayload({
      item: syntheticItem,
      categoryId: base.categoryId,
      initialSize: "8f",
      isDoce: base.flavorType === "doce",
      basePrice: base.prices["8f"],
      getPriceForSize: (s) => base.prices[s],
      isHalfHalf: true,
      flavor2: { name: other.name, tier: other.tier },
    });
  };

  const handleAddConfirmed = (line) => {
    const bordaKey = line.borda?.id || "nb";
    const halfKey = line.isHalfHalf ? "mm" : "s";
    const id = `${halfKey}__${line.item.name}__${line.size}__${bordaKey}`;
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      if (idx >= 0) {
        return prev.map((c, i) => (i === idx ? { ...c, qty: c.qty + line.qty } : c));
      }
      return [
        ...prev,
        {
          id,
          name: line.item.name,
          size: line.size,
          basePrice: line.basePrice,
          borda: line.borda,
          price: line.price,
          qty: line.qty,
          image: line.item.image,
          isHalfHalf: line.isHalfHalf,
          flavor2: line.flavor2,
        },
      ];
    });
    toast.success(`Adicionado: ${line.item.name}`);
  };

  const handleAddBeverage = ({ item }) => {
    const id = `bev__${item.name}`;
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      if (idx >= 0) return prev.map((c, i) => (i === idx ? { ...c, qty: c.qty + 1 } : c));
      return [
        ...prev,
        {
          id,
          name: item.name,
          size: null,
          basePrice: item.price,
          borda: null,
          price: item.price,
          qty: 1,
          image: item.image,
          isHalfHalf: false,
        },
      ];
    });
    toast.success(`Adicionado: ${item.name}`);
  };

  const updateQty = (id, qty) => {
    if (qty <= 0) return setCart((p) => p.filter((c) => c.id !== id));
    setCart((p) => p.map((c) => (c.id === id ? { ...c, qty } : c)));
  };
  const removeItem = (id) => setCart((p) => p.filter((c) => c.id !== id));

  const subtotal = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const itemCount = cart.reduce((s, c) => s + c.qty, 0);

  return (
    <div className="App grain">
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: "hsl(240 5% 8%)",
            color: "hsl(0 0% 98%)",
            border: "1px solid hsl(240 4% 16%)",
          },
        }}
      />

      <div className="relative z-10 max-w-md mx-auto pb-32">
        <Header isOpenNow={open} />
        <Menu
          onRequestAdd={requestAdd}
          onAddBeverage={handleAddBeverage}
          onOpenMeioMeio={openMeioMeio}
        />
      </div>

      {itemCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none" data-testid="floating-cart-wrap">
          <div className="max-w-md mx-auto px-4 pb-4 pt-6 bg-gradient-to-t from-background via-background/95 to-transparent pointer-events-auto">
            <button
              onClick={() => setCartOpen(true)}
              data-testid="floating-cart-btn"
              className="press w-full flex items-center justify-between gap-3 bg-gradient-to-b from-rose-500 to-rose-600 text-white font-semibold px-5 py-3.5 rounded-2xl shadow-[0_16px_40px_-12px_rgba(244,63,94,0.7)]"
            >
              <span className="flex items-center gap-2">
                <span className="relative">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="absolute -top-1.5 -right-2 bg-white text-rose-600 text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                </span>
                <span>Ver sacola</span>
              </span>
              <span className="font-display text-lg" data-testid="floating-cart-total">
                {formatBRL(subtotal)}
              </span>
            </button>
          </div>
        </div>
      )}

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        updateQty={updateQty}
        removeItem={removeItem}
        clearCart={() => setCart([])}
      />

      <BordaModal
        open={!!bordaPayload}
        payload={bordaPayload}
        onClose={() => setBordaPayload(null)}
        onConfirm={handleAddConfirmed}
      />

      <MeioMeioModal open={meioOpen} onClose={() => setMeioOpen(false)} onPicked={handleMeioPicked} />

      <footer className="relative z-10 max-w-md mx-auto px-5 pb-24 pt-4 text-center text-[11px] text-zinc-600">
        © {new Date().getFullYear()} Dona Maria Pizzaria — Feito com amor em Londrina.
      </footer>
    </div>
  );
}

export default App;
